import { IndexTable, Text } from '@shopify/polaris';

/**
 * Tabla de pérdidas/apariciones (cuenta 390) por familia.
 *
 * Filas = depósitos donde la familia tiene saldo neto != 0 en 390.
 * Columnas = variantes (talla/color).
 * Celda = saldo neto (D - H). Negativo = perdida; positivo = aparecida.
 *
 * Si se pasa `deposit`, restringe a ese depósito; si no, muestra todos.
 *
 * Visual: negativos en `tone="critical"`, positivos en `tone="success"`,
 * filas y total con el mismo patrón que `ProductStockTable` para consistencia.
 */
const ProductLossesTable = ({ product, deposit }) => {
  const headings = [{ title: '' }, ...product.variants.map((v) => ({ title: v.title })), { title: 'TOTAL' }];

  if (!product.deposits || !product.deposits.length) {
    product.deposits = product.stock
      .map((s) => s.deposit)
      .filter((d, i, arr) => arr.findIndex((t) => t.id === d.id) === i);
  }

  const visibleDeposits = product.deposits.filter((d) => (deposit ? d.id === deposit.id : true));

  const qtyFor = (depositId, barcode) => {
    const found = product.stock.find((s) => s.barcode === barcode && s.deposit.id === depositId);
    return found ? found.qty : 0;
  };

  // Tono "suave" según el signo del valor.
  const toneOf = (n) => (n < 0 ? 'critical' : n > 0 ? 'success' : 'subdued');

  const rowMarkup = visibleDeposits.map((d, index) => {
    const qtys = product.variants.map((v) => qtyFor(d.id, v.barcode));
    const total = qtys.reduce((acc, cur) => acc + cur, 0);
    const cells = [d.name.toUpperCase(), ...qtys, total];

    return (
      <IndexTable.Row id={`${product.id}-${d.id}`} key={index} position={index} subdued selected={false}>
        {cells.map((cell, i, arr) => {
          const isLabel = i === 0;
          const isTotal = i === arr.length - 1;
          const numericTone = !isLabel ? toneOf(cell) : undefined;
          return (
            <IndexTable.Cell key={i}>
              <Text
                alignment={isLabel ? 'start' : 'center'}
                variant="headingXs"
                fontWeight={isLabel || isTotal ? 'bold' : 'regular'}
                tone={numericTone}
              >
                {cell}
              </Text>
            </IndexTable.Cell>
          );
        })}
      </IndexTable.Row>
    );
  });

  // Fila TOTAL agregada por columna; siempre se muestra (también con un solo
  // depósito) porque el caso de uso es ver el neto de la familia.
  const variantTotals = product.variants.map((v) =>
    visibleDeposits.reduce((acc, d) => acc + qtyFor(d.id, v.barcode), 0),
  );
  const familyTotal = variantTotals.reduce((acc, cur) => acc + cur, 0);

  rowMarkup.push(
    <IndexTable.Row id={`${product.id}-total`} key="total" position={rowMarkup.length} disabled>
      <IndexTable.Cell>
        <Text alignment="start" fontWeight="bold">
          TOTAL
        </Text>
      </IndexTable.Cell>
      {variantTotals.map((t, i) => (
        <IndexTable.Cell key={i}>
          <Text alignment="center" fontWeight="bold" tone={toneOf(t)}>
            {t}
          </Text>
        </IndexTable.Cell>
      ))}
      <IndexTable.Cell>
        <Text alignment="center" fontWeight="bold" tone={toneOf(familyTotal)}>
          {familyTotal}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>,
  );

  return (
    <IndexTable headings={headings} itemCount={rowMarkup.length} selectable={false} lastColumnSticky={true}>
      {rowMarkup}
    </IndexTable>
  );
};

export default ProductLossesTable;
